// src/App.tsx
import React, { useEffect } from 'react';
import './App.css';
import { Route, Routes, useNavigate } from 'react-router-dom';
import Layout from './Components/common/Layout/Layout'; // Assuming Layout is still used if applicable
import PlantDataTable from './Features/Plants/PlantListTable';
import CreatePlant from './Features/Plants/CreatePlant';
import CreatePlantDetails from './Features/Plants/CreatePlantDetails';
import Summary from './Features/Summary/Summary';
import AssetSummaryDetails from './Features/Summary/AssetSummaryDetails';
import Profile from './Features/Profiles/Profile';
import AddUser from './Features/Profiles/AddUser';
import Securities from './Features/Securities/Securities';
import { useDispatch } from 'react-redux';
import { setAmChartsLicense } from './utils/amChartsConfig';
import { login, logout } from './Login/authslice';
import Login from './Login/Login';
import AlertsNotification from './Features/Alerts/AlertsNotification';
import Notification from './Features/Plants/Notification';
import ProtectedRoute from './Components/common/ProtectedRoute';
import GlobalAdminRoute from './Components/common/GlobalAdminRoute';
import SessionTimeout from './Components/SessionTimeout';
import { useAuth } from './contexts/AuthContext';

function App() {
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const { isAuthenticated: authContextAuthenticated, loading } = useAuth();
  
  // Use AuthContext authentication state as the primary source
  const isUserAuthenticated = authContextAuthenticated;
  
  useEffect(() => {
    // Initialize AM Charts license early
    setAmChartsLicense();
    
    const userEmail = localStorage.getItem("loginemail");
    const userRole = localStorage.getItem("userRole") || "";

    
    // Sync Redux state with AuthContext when user is authenticated
    if (userEmail && authContextAuthenticated) {
      dispatch(login({ email: userEmail, role: userRole }));
    }
  }, [dispatch, authContextAuthenticated]);

  const handleLogout = () => {
    dispatch(logout());
    localStorage.clear();
    navigate('/', { replace: true });
  };

  // Show loading while authentication is being determined
  if (loading) {
    return <div>Loading...</div>;
  }

  return (
    <>
      {/* Add SessionTimeout component when user is authenticated */}
      {isUserAuthenticated && <SessionTimeout onLogout={handleLogout} />}
      
      <Routes>
        <Route
          path="/"
          element={
            isUserAuthenticated ? (
              <Layout hideHeader={true}>
                <Summary />
              </Layout>
            ) : (
              <Login
                onLogin={() => {
                  const email = localStorage.getItem("loginemail") || "";
                  const role = localStorage.getItem("userRole") || "";
                  dispatch(login({ email, role }));
                }}
              />
            )
          }
        />
        
        {/* Protected Routes */}
        <Route 
          path="/home" 
          element={
            <ProtectedRoute>
              <Layout hideHeader={true}>
                <Summary />
              </Layout>
            </ProtectedRoute>
          } 
        />
        
        <Route 
          path="/plants" 
          element={
            <ProtectedRoute>
              <Layout>
                <PlantDataTable />
              </Layout>
            </ProtectedRoute>
          } 
        />

        <Route 
          path="/create-plant" 
          element={
            <ProtectedRoute>
              <Layout>
                <CreatePlant />
              </Layout>
            </ProtectedRoute>
          } 
        />
        
        <Route 
          path="/create-plant/:plantId" 
          element={
            <ProtectedRoute>
              <Layout>
                <CreatePlant />
              </Layout>
            </ProtectedRoute>
          } 
        />
        
        <Route 
          path="/create-plant-details" 
          element={
            <ProtectedRoute>
              <Layout>
                <CreatePlantDetails />
              </Layout>
            </ProtectedRoute>
          } 
        />
        
        <Route 
          path="/summary" 
          element={
            <ProtectedRoute>
              <Layout>
                <Summary />
              </Layout>
            </ProtectedRoute>
          } 
        />
        
        <Route 
          path="/asset-details/:assetId" 
          element={
            <ProtectedRoute>
              <Layout>
                <AssetSummaryDetails />
              </Layout>
            </ProtectedRoute>
          } 
        />
        
        <Route 
          path="/users" 
          element={
            <ProtectedRoute>
              <Layout>
                <Profile />
              </Layout>
            </ProtectedRoute>
          } 
        />
        
        <Route 
          path="/users/add" 
          element={
            <ProtectedRoute>
              <Layout>
                <AddUser />
              </Layout>
            </ProtectedRoute>
          } 
        />
        
        <Route 
          path="/users/edit/:userId" 
          element={
            <ProtectedRoute>
              <Layout>
                <AddUser />
              </Layout>
            </ProtectedRoute>
          } 
        />
        
        <Route 
          path="/create-plant-details/notification" 
          element={
            <ProtectedRoute>
              <Layout>
                <Notification />
              </Layout>
            </ProtectedRoute>
          } 
        />
        
        <Route 
          path="/alerts" 
          element={
            <ProtectedRoute>
              <Layout>
                <AlertsNotification />
              </Layout>
            </ProtectedRoute>
          } 
        />
        
        <Route 
          path="/securities" 
          element={
            <ProtectedRoute>
              <GlobalAdminRoute>
                <Layout>
                  <Securities />
                </Layout>
              </GlobalAdminRoute>
            </ProtectedRoute>
          } 
        />
      </Routes>
    </>
  );
}

export default App;